import { useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ScheduledArchivationAlert } from '@/components/scheduled-archivation-alert';
import { Form } from '@/components/ui/form';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { DatePicker } from '@/modules/common/components/molecules/date/date-picker';
import { invalidateTeamMemberWithRelatedQueries } from '@/modules/common/utils/query-invalidation';
import { archiveTeamMember, getAvailableArchivationDate } from '@/modules/team/api/team.api';
import { TeamMember } from '@/modules/team/types/team-member';
import { formatDisplayDate } from '@/utils/date-formatters';

interface ArchiveTeamMemberModalProps {
  teamMember: TeamMember;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

const archiveSchema = z.object({
  archiveDate: z.date({
    required_error: 'authenticated.team:archive.archiveDateRequired',
  }),
});

type ArchiveFormData = z.infer<typeof archiveSchema>;

/**
 * A comprehensive team member archiving modal with date validation and form integration.
 * Provides controlled archiving workflow with proper date constraints and user feedback.
 *
 * Mirrors the project archive modal functionality but adapted for team members.
 */
export function ArchiveTeamMemberModal({
  teamMember,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: ArchiveTeamMemberModalProps) {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const queryClient = useQueryClient();

  const form = useForm<ArchiveFormData>({
    resolver: zodResolver(archiveSchema),
    mode: 'onChange',
    defaultValues: {
      archiveDate: undefined,
    },
  });

  const archiveTeamMemberMutation = useMutation({
    mutationFn: ({ memberId, archiveDate }: { memberId: string; archiveDate: Date }) =>
      archiveTeamMember({ memberId, archiveDate }),
    onSuccess: async () => {
      await invalidateTeamMemberWithRelatedQueries(queryClient, teamMember.id);
      form.reset();
      onSuccess?.();
    },
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine the actual modal open state (external or internal)
  const actualModalOpen = open !== undefined ? open : isModalOpen;

  // Fetch available archivation date for archive date validation
  const {
    data: availableArchivationDate,
    isLoading: isLoadingAvailableDate,
    error: availableDateError,
  } = useQuery({
    queryKey: ['available-archivation-date', teamMember.id],
    queryFn: () => getAvailableArchivationDate(teamMember.id),
    enabled: actualModalOpen,
  });

  // Determine effective available archivation date based on loading/error states
  const effectiveAvailableArchivationDate = useMemo(() => {
    if (availableDateError) {
      // If there's an error, default to today
      return new Date();
    }
    return availableArchivationDate;
  }, [availableArchivationDate, availableDateError]);

  const handleArchive = async (closeModal: () => void) => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const formData = form.getValues();

    try {
      await archiveTeamMemberMutation.mutateAsync({
        memberId: teamMember.id,
        archiveDate: formData.archiveDate,
      });
      closeModal();
    } catch (error) {
      // Error handled by mutation, keep modal open for retry
    }
  };

  // Function to disable dates before the available archivation date
  const isDateDisabled = (date: Date): boolean => {
    if (!effectiveAvailableArchivationDate) return false;

    // Disable dates that are before the available archivation date
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const availableOnly = new Date(
      effectiveAvailableArchivationDate.getFullYear(),
      effectiveAvailableArchivationDate.getMonth(),
      effectiveAvailableArchivationDate.getDate()
    );

    return dateOnly < availableOnly;
  };

  const archiveDate = form.watch('archiveDate');
  const isFormValid = !!archiveDate;

  const messageContent = (
    <div className="space-y-4">
      {teamMember.nextArchiveDate && (
        <ScheduledArchivationAlert
          message={t('authenticated.team:scheduledArchive.willBeArchivedOn', {
            date: formatDisplayDate(teamMember.nextArchiveDate),
          })}
          className="mb-0"
        />
      )}
      <div>
        <Trans
          i18nKey="authenticated.team:archive.archiveTeamMemberMessage"
          values={{ name: teamMember.name }}
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.team:archive.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.team:archive.memberWillBeHidden')}</li>
          <li>{t('authenticated.team:archive.dataWillBePreserved')}</li>
          <li>{t('authenticated.team:archive.canBeRestored')}</li>
          <li>{t('authenticated.team:archive.noTracking')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.team:archive.archiveTeamMember')}
      message={messageContent}
      confirmText={
        archiveTeamMemberMutation.isPending
          ? t('authenticated.team:archive.archiving')
          : t('authenticated.team:archive.archiveTeamMember')
      }
      confirmVariant="default"
      onConfirm={handleArchive}
      isLoading={archiveTeamMemberMutation.isPending}
      disabled={!isFormValid}
      open={actualModalOpen}
      onOpen={onOpen || (() => setIsModalOpen(true))}
      onClose={
        onClose ||
        (() => {
          setIsModalOpen(false);
          form.reset();
        })
      }
    >
      <Form {...form}>
        <form className="space-y-4">
          <DatePicker
            name="archiveDate"
            control={form.control}
            label={t('authenticated.team:archive.archiveDate')}
            dateFormat="D MMM, YYYY"
            disabled={isDateDisabled}
            inputDisabled={isLoadingAvailableDate}
            isLoading={isLoadingAvailableDate}
          />
        </form>
      </Form>
    </ConfirmationModal>
  );
}
